import React, { useState, useEffect, useRef } from 'react';
import './DynamicTitle.css';

interface Point {
  x: number;
  y: number;
}

interface DynamicTitleProps {
  // No props needed - using internal default paths only
}

const DynamicTitle: React.FC<DynamicTitleProps> = () => {
  // Timing parameters - easily adjustable
  const DRAW_SPEED = 0.01; // How fast the line draws (0.01 = slow, 0.05 = fast)
  const DRAW_INTERVAL = 40; // ms between draw updates (lower = smoother)
  const TEXT_START_DELAY = 0; // ms delay after line finishes before text starts typing
  const NAME_TYPING_SPEED = 100; // ms between each character in name
  const TITLE_TYPING_DELAY = 0; // ms delay between name and title typing (was 10)
  const TITLE_TYPING_SPEED = 80; // ms between each character in title
  const SVG_MOUNT_DELAY = 50; // ms to wait for SVG to mount
  const NUM_CONTROL_POINTS = 10; // Number of control points to show along the path
  
  // Mouse/touch sensitivity scaling factors
  const X_SCALE_FACTOR = 1.0; // Adjust X-axis sensitivity (1.0 = normal)
  const Y_SCALE_FACTOR = 1; // Adjust Y-axis sensitivity (2.0 = double sensitivity for better tracking)
  
  const [orbSpeed, setOrbSpeed] = useState<'fast' | 'slow'>('fast');
  const ORB_CYCLE_TIME = orbSpeed === 'fast' ? 2500 : 4000; // ms for one complete orb cycle

  const [lineDrawProgress, setLineDrawProgress] = useState<number>(0);
  const [displayedName, setDisplayedName] = useState<string>('');
  const [displayedTitle, setDisplayedTitle] = useState<string>('');
  const [showText, setShowText] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(true);
  const [controlPoints, setControlPoints] = useState<Point[]>([]);
  const [dynamicFontSize, setDynamicFontSize] = useState<number>(40);
  const [isHoveringLine, setIsHoveringLine] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [textComplete, setTextComplete] = useState<boolean>(false);
  const [orbPlaying, setOrbPlaying] = useState<boolean>(false);
  const [wasPlayingBeforeDrag, setWasPlayingBeforeDrag] = useState<boolean>(false);
  const [orbPosition, setOrbPosition] = useState<Point>({ x: 0, y: 0 });
  const [orbOpacity, setOrbOpacity] = useState<number>(0);
  const [orbScale, setOrbScale] = useState<number>(1);
  const [activeControlPoint, setActiveControlPoint] = useState<number>(-1);
  const [showButtons, setShowButtons] = useState<boolean>(false);
  const [svgOpacity, setSvgOpacity] = useState<number>(1);
  const [hintPos, setHintPos] = useState<{ left: number; top: number } | null>(null);
  const [showHint, setShowHint] = useState<boolean>(true);
  const [hintState, setHintState] = useState<'start' | 'open' | 'closed' | null>(null);
  const hintTimersRef = useRef<number[] | null>(null);
  const loopCounterRef = useRef<number>(0);
  const MAX_LOOPS = 8; // play 3 times (start+open+closed = 2 cycles per play)
  const START_OFFSET = 0; // px below the node to start from
  const CLOSED_OFFSET = 28; // px to move down after closing
  const START_DURATION = 300; // ms fade-in & move-up
  const OPEN_DURATION = 400; // ms visible while open (shortened)
  const CLOSED_DURATION = 700; // ms closed state including fade-out/move
  const [hintFading, setHintFading] = useState<boolean>(false);
  const [hintVisible, setHintVisible] = useState<boolean>(false);
  const [viewBoxSize, setViewBoxSize] = useState<string>("-50 -50 1100 400");
  const svgRef = useRef<SVGSVGElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const orbAnimationRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const lastNotePositionRef = useRef<number>(0);
  const scaleAnimationRef = useRef<number | null>(null);
  const controlPointTimeoutRef = useRef<number | null>(null);

  const fullName = 'Gidi Rubin';
  const fullTitle = 'Developer, Designer, Engineer';
  
  // Default SVG paths - only using these internal ones
  const defaultSvgPaths = [
    "M.35,123.02C104.35,22.02,288.35-1.98,400.35,68.02c142.64,89.15,97.67,219.57,189,255,107.18,41.58,229.94-60.28,239-68C939.94,159.96,937.95,19.64,937.35.02",
    "M.25,128.27C115.63,60.44,218.77,33.29,285.97,21.13,397.45.95,561.55-28.75,675.25,68.27c40.17,34.28,102.98,109.07,83.57,171.43-30.86,99.14-262.32,143.69-414.29,60-74-40.75-146.53-121.48-127.86-159.29,26.79-54.24,231.92-1.11,273.57,10",
    "M.36,250.47c33.41,33.85,98.93,90.19,203.93,123.12,222.39,69.75,540.78,9.09,601.61-130.16,28.28-64.74-.55-143.18-63.03-189.25C610.24-43.61,327.33,5.44,289.57,95.69c-4.37,10.44-22.48,62.12,82.5,148.45",
    "M.35,114.92C132.24-11.84,305.75-30.73,405.35,45.63c81.39,62.4,124.1,196.58,76.43,256.43-70.41,88.39-340.18,17.34-351.43-62.14-15.28-107.94,434.56-313.85,537.14-210,41.83,42.35,20.84,131.09-1.43,195.71",
    // "M.39,160.91C27.56,126.65,112.33,28.99,251.11,5.91c134.8-22.42,317.64,26,337.14,125,14.63,74.26-65.4,163-148.57,194.29-155.6,58.53-353.36-72.35-347.14-149.29,5.21-64.41,156.04-123.96,276.43-95.71,48.53,11.39,134.32,46.95,134.29,97.14-.03,43.06-63.21,91.35-158.57,104.29",
// "M531.37.5c-61.71,1.42-143.61,7.42-236.47,26.43C195.19,47.34,8.21,85.62.73,143.36c-12.55,96.96,485.38,215.14,500.37,192.14,10.12-15.52-207.12-84.12-192.01-139.29,14.38-52.5,231.98-66.42,496.59-64.29",
// "M200.82,377.45c-24-7.74-61.33-22.95-98.57-53.57-5.62-4.62-113.41-95.07-100.71-174.29C18.01,46.84,233.37-13.18,403.68,3.17c129.49,12.43,329.07,78.25,334.29,181.43,4.64,91.85-145.76,193.21-284.29,195-170.75,2.2-329.1-146.8-308.57-215,9.4-31.24,60.68-59.99,225.71-62.14",
"M.42,328.93C39.24,269.03,185.42,46.53,260.34,4.65c1.4-.78,4.8-2.64,9.84-3.57,86.39-16.05,270.98,309.19,219.75,345-45.07,31.51-308.3-136.07-280.42-211.43,18.78-50.77,174.39-72.34,600.2-25.71",
"M.29,183.54c178.57,130,557.33,123.09,594.29,25,24.18-64.19-5.66-156.48-65.71-191.43C418.24-47.27,217.66,90.25,223.87,177.82c6.5,91.74,242.66,167.14,430,163.57,41.83-.8,77.03-5.45,102.86-10",
  ];

  // Always use the default paths
  const availablePaths = defaultSvgPaths;
  
  // Path selection - can be changed with shuffle button
  const [selectedPathIndex, setSelectedPathIndex] = useState<number>(() => {
    const randomIndex = Math.floor(Math.random() * availablePaths.length);
    console.log(`Selected path ${randomIndex + 1} of ${availablePaths.length}:`, availablePaths[randomIndex]);
    return randomIndex;
  });
  
  const selectedPath = availablePaths[selectedPathIndex];

  // Original path for reference (now the randomly selected one)
  const originalPath = selectedPath;

  // Generate smooth curve through control points
  const generatePathFromPoints = (points: Point[]): string => {
    if (points.length < 2) return originalPath;
    
    let path = `M${points[0].x},${points[0].y}`;
    
    if (points.length === 2) {
      // Simple line for 2 points
      path += `L${points[1].x},${points[1].y}`;
    } else {
      // Improved smooth curve using catmull-rom inspired approach
      for (let i = 1; i < points.length; i++) {
        const prev = points[i - 1];
        const curr = points[i];
        const next = points[i + 1];
        const prevPrev = points[i - 2];
        
        if (i === 1) {
          // First curve segment - gentler control points
          const cp1x = prev.x + (curr.x - prev.x) * 0.25;
          const cp1y = prev.y + (curr.y - prev.y) * 0.25;
          const cp2x = curr.x - (next ? (next.x - prev.x) * 0.15 : (curr.x - prev.x) * 0.25);
          const cp2y = curr.y - (next ? (next.y - prev.y) * 0.15 : (curr.y - prev.y) * 0.25);
          path += `C${cp1x},${cp1y} ${cp2x},${cp2y} ${curr.x},${curr.y}`;
        } else if (i === points.length - 1) {
          // Last curve segment - gentler control points
          const cp1x = prev.x + (prevPrev ? (curr.x - prevPrev.x) * 0.15 : (curr.x - prev.x) * 0.25);
          const cp1y = prev.y + (prevPrev ? (curr.y - prevPrev.y) * 0.15 : (curr.y - prev.y) * 0.25);
          const cp2x = curr.x - (curr.x - prev.x) * 0.25;
          const cp2y = curr.y - (curr.y - prev.y) * 0.25;
          path += `C${cp1x},${cp1y} ${cp2x},${cp2y} ${curr.x},${curr.y}`;
        } else {
          // Middle curve segments - smoother transitions
          const cp1x = prev.x + (curr.x - prevPrev.x) * 0.15;
          const cp1y = prev.y + (curr.y - prevPrev.y) * 0.15;
          const cp2x = curr.x - (next.x - prev.x) * 0.15;
          const cp2y = curr.y - (next.y - prev.y) * 0.15;
          path += `C${cp1x},${cp1y} ${cp2x},${cp2y} ${curr.x},${curr.y}`;
        }
      }
    }
    
    return path;
  };

  // Use dynamic path only if control points have been modified, otherwise use original
  const [hasModifiedPoints, setHasModifiedPoints] = useState<boolean>(false);
  const scribblePath = hasModifiedPoints ? generatePathFromPoints(controlPoints) : originalPath;
  // Calculate dynamic font size based on path length
  const calculateFontSize = (pathData: string): number => {
    // Create a temporary path to measure length
    const tempPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    tempPath.setAttribute('d', pathData);
    document.body.appendChild(tempPath);
    
    const pathLength = tempPath.getTotalLength();
    document.body.removeChild(tempPath);
    
    // Calculate font size based on path length
    // Longer paths can accommodate larger text
    const baseFontSize = 60;
    const minFontSize = 44;
    const maxFontSize = 70;
    
    // Estimate text length (name + title with spaces)
    const totalTextLength = fullName.length + fullTitle.length;
    
    // Calculate optimal font size to prevent overlap
    // This is a rough estimation - adjust multiplier as needed
    const optimalSize = (pathLength / totalTextLength) * 0.8;
    
    return Math.max(minFontSize, Math.min(maxFontSize, optimalSize));
  };

  // Initialize control points evenly distributed along the path
  useEffect(() => {
    if (pathRef.current && (controlPoints.length === 0 || !hasModifiedPoints)) {
      // Create a temporary path element to get points from the selected path
      const tempPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      tempPath.setAttribute('d', selectedPath);
      document.body.appendChild(tempPath);
      
      const totalLength = tempPath.getTotalLength();
      const points: Point[] = [];
      
      // Parse viewBox to get boundaries
      const viewBoxParts = viewBoxSize.split(' ').map(Number);
      const [minX, minY, width, height] = viewBoxParts;
      const maxX = minX + width;
      const maxY = minY + height;
      
      // Create evenly spaced control points along the selected path, constrained to viewBox
      for (let i = 0; i < NUM_CONTROL_POINTS; i++) {
        const position = (i / (NUM_CONTROL_POINTS - 1)) * totalLength;
        const point = tempPath.getPointAtLength(position);
        
        // Constrain points to viewBox boundaries
        const constrainedX = Math.max(minX, Math.min(maxX, point.x));
        const constrainedY = Math.max(minY, Math.min(maxY, point.y));
        
        points.push({ x: constrainedX, y: constrainedY });
      }
      
      document.body.removeChild(tempPath);
      setControlPoints(points);
      
      // Calculate initial font size
      const initialFontSize = calculateFontSize(selectedPath);
      setDynamicFontSize(initialFontSize);
    }
  }, [controlPoints.length, selectedPath, hasModifiedPoints, viewBoxSize]);

  // Compute hint position (pixel coordinates) for the hand icon based on a target control point
  useEffect(() => {
    const computeHint = () => {
      const svg = svgRef.current;
      if (!svg || controlPoints.length === 0) {
        setHintPos(null);
        return;
      }

      // Always target the first control point (index 0)
      const target = controlPoints[0];

      // Preferred, robust method: convert SVG user-space point to screen coordinates
      try {
        // createSVGPoint may not exist in some server-side contexts; guard accordingly
        const pt = (svg as any).createSVGPoint();
        pt.x = target.x;
        pt.y = target.y;

        const screenCTM = svg.getScreenCTM();
        if (screenCTM) {
          const screenPt = pt.matrixTransform(screenCTM);
          const parent = svg.parentElement as HTMLElement | null;
          const parentRect = parent ? parent.getBoundingClientRect() : { left: 0, top: 0 } as DOMRect;

          const left = screenPt.x - parentRect.left;
          const top = screenPt.y - parentRect.top;
          setHintPos({ left, top });
          return;
        }
      } catch (err) {
        // fall through to bounding-box fallback
        // console.debug('SVG point transform failed, falling back to rect scaling', err);
      }

      // Fallback: approximate using bounding client rect and viewBox scaling
      const svgRect = svg.getBoundingClientRect();
      const parent = svg.parentElement as HTMLElement | null;
      const parentRect = parent ? parent.getBoundingClientRect() : svgRect;
      const viewBox = svg.viewBox.baseVal;
      const scaleX = svgRect.width / viewBox.width;
      const scaleY = svgRect.height / viewBox.height;

      const xInParent = (target.x - viewBox.x) * scaleX + (svgRect.left - parentRect.left);
      const yInParent = (target.y - viewBox.y) * scaleY + (svgRect.top - parentRect.top);
      setHintPos({ left: xInParent, top: yInParent });
    };

    computeHint();
    window.addEventListener('resize', computeHint);
    // also update when control points change
    return () => window.removeEventListener('resize', computeHint);
  }, [controlPoints, viewBoxSize]);

  // Manage hint cycle: open -> closed -> hide, repeated every 3s. Hide while dragging.
  useEffect(() => {
    // Clear any existing timers
    if (hintTimersRef.current) {
      hintTimersRef.current.forEach((id) => clearTimeout(id));
      hintTimersRef.current = null;
    }

    if (!showHint || !hintPos || isDragging) {
      setHintState(null);
      return;
    }

    const startCycle = () => {
      setHintFading(false);
      setHintVisible(false);
      // Start at a slightly lower position and prepare to fade in
      setHintState('start');
      // Trigger fade-in shortly after mounting the state so transition runs
      const visTrigger = window.setTimeout(() => setHintVisible(true), 20);

      const t1 = window.setTimeout(() => {
        setHintState('open');
      }, START_DURATION);

      // Switch to closed hand image first (don't move yet)
      const tCloseStart = window.setTimeout(() => {
        setHintState('closed');
        setHintFading(false);
      }, START_DURATION + OPEN_DURATION);
      
      // After showing the closed hand briefly (500ms pause), start moving it down and fading
      const tMoveDown = window.setTimeout(() => {
        setHintFading(true);
      }, START_DURATION + OPEN_DURATION + 500);

      // After CLOSED_DURATION from the close start, end this cycle
      const tCloseEnd = window.setTimeout(() => {
        setHintState(null);
        setHintVisible(false);
        loopCounterRef.current = (loopCounterRef.current || 0) + 1;
        if (loopCounterRef.current < MAX_LOOPS) {
          const gap = 200;
          const tNext = window.setTimeout(() => startCycle(), gap);
          hintTimersRef.current = [visTrigger, t1, tCloseStart, tMoveDown, tCloseEnd, tNext];
        } else {
          setShowHint(false);
          hintTimersRef.current = [visTrigger, t1, tCloseStart, tMoveDown, tCloseEnd];
        }
      }, START_DURATION + OPEN_DURATION + CLOSED_DURATION);

      hintTimersRef.current = [t1, tCloseStart, tMoveDown];
    };

    startCycle();

    return () => {
      if (hintTimersRef.current) {
        hintTimersRef.current.forEach((id) => clearTimeout(id));
        hintTimersRef.current = null;
      }
    };
  }, [showHint, hintPos, isDragging]);

  // Handle responsive viewBox sizing
  useEffect(() => {
    const updateViewBox = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth <= 768) {
        // Mobile: larger viewBox to show more of the path
        setViewBoxSize("-100 -100 1200 600");
      } else if (screenWidth <= 1024) {
        // Tablet: medium viewBox
        setViewBoxSize("-75 -75 1150 500");
      } else {
        // Desktop: smaller viewBox for fullscreen
        setViewBoxSize("-25 -25 1050 350");
      }
    };

    // Set initial viewBox
    updateViewBox();

    // Add resize listener
    window.addEventListener('resize', updateViewBox);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', updateViewBox);
    };
  }, []);

  const handlePointDrag = (index: number, newX: number, newY: number) => {
    if (!isEditing) return;
    
    // Pause orb animation if it's playing to prevent heavy processing
    // Check both orbOpacity and orbPlaying to capture intended state
    if ((orbOpacity > 0 || orbPlaying) && !isDragging) {
      console.log('Setting wasPlayingBeforeDrag = true, orbOpacity:', orbOpacity, 'orbPlaying:', orbPlaying);
      setWasPlayingBeforeDrag(true);
      setOrbPlaying(false);
      setOrbOpacity(0);
    }
    
    // Parse current viewBox to get dynamic boundaries
    const viewBoxParts = viewBoxSize.split(' ').map(Number);
    const [minX, minY, width, height] = viewBoxParts;
    const maxX = minX + width;
    const maxY = minY + height;
    
    // Constrain the coordinates to the current viewBox boundaries
    const constrainedX = Math.max(minX, Math.min(maxX, newX));
    const constrainedY = Math.max(minY, Math.min(maxY, newY));
    
    const newPoints = [...controlPoints];
    newPoints[index] = { x: constrainedX, y: constrainedY };
    setControlPoints(newPoints);
    setHasModifiedPoints(true); // Mark as modified when points are dragged
    
    // Recalculate font size when path changes
    const newPath = generatePathFromPoints(newPoints);
    const newFontSize = calculateFontSize(newPath);
    setDynamicFontSize(newFontSize);
  };

  // Find the closest control point to a given position
  const findClosestControlPoint = (x: number, y: number): number => {
    if (controlPoints.length === 0) return 0;
    
    let closestIndex = 0;
    let closestDistance = Infinity;
    
    controlPoints.forEach((point, index) => {
      const distance = Math.sqrt(Math.pow(x - point.x, 2) + Math.pow(y - point.y, 2));
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });
    
    console.log(`Clicked at (${x.toFixed(1)}, ${y.toFixed(1)}), closest point ${closestIndex} at (${controlPoints[closestIndex].x.toFixed(1)}, ${controlPoints[closestIndex].y.toFixed(1)}), distance: ${closestDistance.toFixed(1)}`);
    
    return closestIndex;
  };

  // Handle mouse down on the SVG - no more snap to nearest point
  const handleSvgMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    // SVG click handling disabled - control points can only be dragged directly
    return;
  };

  // Calculate opacity for control points
  const getControlPointOpacity = (): number => {
    if (isDragging || isHoveringLine) {
      return 1; // Fully visible when dragging or hovering line
    }
    return 0.6; // Almost transparent otherwise
  };

  // Initialize audio context for discrete note playing
  const initializeAudio = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
  };

  // Play a discrete note based on position and highlight control point
  const playNoteAtPosition = (x: number, y: number, controlPointIndex: number) => {
    if (!audioContextRef.current) return;
    
    // Define pleasant musical scale (C major pentatonic) spanning 2 octaves
    const frequencies = [
      261.63, 293.66, 329.63, 392.00, 440.00, // C4, D4, E4, G4, A4 (lower octave)
      523.25, 587.33, 659.25, 783.99, 880.00  // C5, D5, E5, G5, A5 (higher octave)
    ];
    
    // Map X position (0-1000) left-to-right to progress through the full scale
    const normalizedX = Math.max(0, Math.min(1000, x)) / 1000;
    const noteIndex = Math.floor(normalizedX * frequencies.length);
    const frequency = frequencies[Math.min(noteIndex, frequencies.length - 1)];
    
    // Y position no longer affects pitch - only X position determines the note
    const normalizedY = Math.max(0, Math.min(300, y)) / 300;
    
    // Create oscillator for this note
    const oscillator = audioContextRef.current.createOscillator();
    const gainNode = audioContextRef.current.createGain();
    const filter = audioContextRef.current.createBiquadFilter();
    
    // Configure nodes
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(frequency, audioContextRef.current.currentTime);
    
    filter.type = 'lowpass';
    // Filter also follows intuitive mapping: higher on canvas = brighter sound
    filter.frequency.setValueAtTime(800 + ((1 - normalizedY) * 1200), audioContextRef.current.currentTime);
    filter.Q.setValueAtTime(0.5, audioContextRef.current.currentTime);
    
    // Envelope for pleasant note
    const now = audioContextRef.current.currentTime;
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.15, now + 0.05); // Quick attack
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.5); // Decay over 0.5 seconds
    
    // Connect nodes
    oscillator.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(audioContextRef.current.destination);
    
    // Play note
    oscillator.start(now);
    oscillator.stop(now + 0.5); // Stop after 0.5 seconds
    
    // Trigger orb scale animation and highlight control point
    triggerOrbScale();
    highlightControlPoint(controlPointIndex);
  };

  // Highlight a control point for half a second
  const highlightControlPoint = (index: number) => {
    setActiveControlPoint(index);
    
    // Clear any existing timeout
    if (controlPointTimeoutRef.current) {
      clearTimeout(controlPointTimeoutRef.current);
    }
    
    // Clear highlight after 500ms
    controlPointTimeoutRef.current = setTimeout(() => {
      setActiveControlPoint(-1);
    }, 500) as any;
  };

  // Trigger orb scaling animation
  const triggerOrbScale = () => {
    setOrbScale(1.8); // Scale up
    
    // Cancel any existing scale animation
    if (scaleAnimationRef.current) {
      clearTimeout(scaleAnimationRef.current);
    }
    
    // Scale back down after a short delay
    scaleAnimationRef.current = setTimeout(() => {
      setOrbScale(1);
    }, 150) as any;
  };

  // Clean up audio resources
  const cleanupAudio = () => {
    try {
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      if (scaleAnimationRef.current) {
        clearTimeout(scaleAnimationRef.current);
        scaleAnimationRef.current = null;
      }
      if (controlPointTimeoutRef.current) {
        clearTimeout(controlPointTimeoutRef.current);
        controlPointTimeoutRef.current = null;
      }
    } catch (error) {
      // Ignore errors during cleanup
      console.log('Audio cleanup completed');
    }
  };

  // Start orb animation along the path
  const startOrbAnimation = () => {
    if (!pathRef.current) return;
    initializeAudio(); // Initialize audio when starting
    setOrbPlaying(true);
  };

  // Stop orb animation
  const stopOrbAnimation = () => {
    setOrbPlaying(false);
    setOrbOpacity(0);
    setOrbScale(1);
    setActiveControlPoint(-1);
    // Don't reset wasPlayingBeforeDrag here - let drag handlers manage it
    lastNotePositionRef.current = -1; // Reset note position
    cleanupAudio(); // Clean up audio when stopping
  };

  // Handle orb animation with useEffect
  useEffect(() => {
    if (!orbPlaying || !pathRef.current) return;
    
    const pathLength = pathRef.current.getTotalLength();
    const startTime = Date.now();
    const totalControlPoints = controlPoints.length;
    
    const animateOrb = () => {
      if (!pathRef.current || !orbPlaying) return;
      
      const elapsed = Date.now() - startTime;
      const progress = (elapsed % ORB_CYCLE_TIME) / ORB_CYCLE_TIME; // Loop the animation
      
      // Get position along path
      const position = pathRef.current.getPointAtLength(progress * pathLength);
      setOrbPosition({ x: position.x, y: position.y });
      
      // Simple evenly-spaced note triggering - much faster than distance calculations
      if (totalControlPoints > 0) {
        const noteInterval = 1 / totalControlPoints; // Evenly space notes
        const currentNoteIndex = Math.floor(progress / noteInterval);
        
        // Check if we've reached a new control point
        if (currentNoteIndex !== lastNotePositionRef.current && currentNoteIndex < totalControlPoints) {
          lastNotePositionRef.current = currentNoteIndex;
          playNoteAtPosition(controlPoints[currentNoteIndex].x, controlPoints[currentNoteIndex].y, currentNoteIndex);
        }
      }
      
      // Calculate sine wave opacity based on position along path (0 to 1)
      // Fade in from 0 to 0.25, stay visible from 0.25 to 0.75, fade out from 0.75 to 1
      let opacity: number;
      if (progress < 0.25) {
        // Fade in: 0 to 0.8 opacity over first 25% of path
        opacity = (progress / 0.25) * 0.8;
      } else if (progress < 0.75) {
        // Stay visible: full 0.8 opacity for middle 50% of path
        opacity = 0.8;
      } else {
        // Fade out: 0.8 to 0 opacity over last 25% of path
        opacity = (1 - progress) / 0.25 * 0.8;
      }
      setOrbOpacity(opacity);
      
      // Continue animation
      orbAnimationRef.current = requestAnimationFrame(animateOrb);
    };
    
    orbAnimationRef.current = requestAnimationFrame(animateOrb);
    
    // Cleanup function
    return () => {
      if (orbAnimationRef.current) {
        cancelAnimationFrame(orbAnimationRef.current);
        orbAnimationRef.current = null;
      }
    };
  }, [orbPlaying, ORB_CYCLE_TIME, controlPoints, scribblePath]);

  // Auto-resume when orb opacity goes to 0 and we should be playing
  useEffect(() => {
    if (orbOpacity === 0 && wasPlayingBeforeDrag && !orbPlaying && !isDragging) {
      console.log('Auto-resuming orb animation - text is now fully opaque');
      setWasPlayingBeforeDrag(false);
      setTimeout(() => {
        initializeAudio();
        setOrbPlaying(true);
      }, 50);
    }
  }, [orbOpacity, wasPlayingBeforeDrag, orbPlaying, isDragging]);

  // Cleanup audio on component unmount
  useEffect(() => {
    return () => {
      cleanupAudio();
    };
  }, []);

  // Toggle orb animation
  const toggleOrbAnimation = () => {
    console.log('Toggle clicked - orbOpacity:', orbOpacity, 'orbPlaying:', orbPlaying);
    if (orbOpacity > 0) {
      stopOrbAnimation();
    } else {
      startOrbAnimation();
    }
  };

  // Toggle orb speed
  const toggleOrbSpeed = () => {
    setOrbSpeed(orbSpeed === 'fast' ? 'slow' : 'fast');
  };

  // Shuffle to next SVG path
  const shufflePath = () => {
    // Start fade out
    setSvgOpacity(0);
    
    // Stop orb animation during shuffle
    if (orbPlaying) {
      setOrbPlaying(false);
      setOrbOpacity(0);
    }
    
    setTimeout(() => {
      const nextIndex = (selectedPathIndex + 1) % availablePaths.length;
      setSelectedPathIndex(nextIndex);
      console.log(`Shuffled to path ${nextIndex + 1} of ${availablePaths.length}`);
      
      // Reset control points and modified state to use the new original path
      setControlPoints([]);
      setHasModifiedPoints(false);
      
      // Fade back in after a short delay
      setTimeout(() => {
        setSvgOpacity(1);
      }, 100);
    }, 300); // Wait for fade out to complete
  };

  useEffect(() => {
    // Reset everything
    setLineDrawProgress(0);
    setDisplayedName('');
    setDisplayedTitle('');
    setShowText(false);
    setShowButtons(false); // Reset button visibility

    // Wait for SVG to mount, then start animation
    setTimeout(() => {
      // Phase 1: Draw the scribble line with sine easing
      let drawProgress = 0;
      let textHasStarted = false; // Use local variable instead of state
      
      const drawInterval = setInterval(() => {
        drawProgress += DRAW_SPEED;
        
        // Apply sine wave easing (slower ease in/out)
        const easedProgress = 0.5 - 0.5 * Math.cos(drawProgress * Math.PI);
        setLineDrawProgress(easedProgress);
        
        // Start text typing when line is 50% complete
        if (drawProgress >= 0.2 && !textHasStarted) {
          textHasStarted = true;
          
          setTimeout(() => {
            setShowText(true);
            
            // Type the name first
            let nameIndex = 0;
            const nameInterval = setInterval(() => {
              setDisplayedName(fullName.substring(0, nameIndex + 1));
              nameIndex++;
              
              if (nameIndex >= fullName.length) {
                clearInterval(nameInterval);
                
                // After name is complete, start typing title immediately
                let titleIndex = 0;
                const titleInterval = setInterval(() => {
                  setDisplayedTitle(fullTitle.substring(0, titleIndex + 1));
                  titleIndex++;
                  
                  if (titleIndex >= fullTitle.length) {
                    clearInterval(titleInterval);
                    setTextComplete(true);
                  }
                }, TITLE_TYPING_SPEED);
              }
            }, NAME_TYPING_SPEED);
          }, TEXT_START_DELAY);
        }
        
        if (drawProgress >= 1) {
          clearInterval(drawInterval);
          // Show buttons after line is complete with a small delay
          setTimeout(() => {
            setShowButtons(true);
          }, 300);
        }
      }, DRAW_INTERVAL);

      return () => {
        clearInterval(drawInterval);
      };
    }, SVG_MOUNT_DELAY);
  }, []);

  return (
    <div className="dynamic-title" style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center',
      width: '100%',
      position: 'relative'
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        width: '100%',
        position: 'relative'
      }}>
        <svg 
          ref={svgRef}
          width="1200" 
          height="700" 
          viewBox={viewBoxSize}
          className="title-svg"
          onMouseDown={handleSvgMouseDown}
          style={{ 
            cursor: isEditing ? 'grab' : 'default',
            touchAction: isDragging ? 'none' : 'auto', // Prevent scrolling while dragging
            maxWidth: '100%',
            height: 'auto',
            opacity: svgOpacity,
            transition: 'opacity 0.3s ease'
          }}
          onTouchStart={(e) => {
            // Prevent default touch behaviors on SVG when not dragging control points
            if (isDragging) {
              e.preventDefault();
            }
          }}
        >
        {/* The scribble path - draws with sine easing and stays visible */}
        <path
          ref={pathRef}
          d={scribblePath}
          stroke="#bababaff"
          strokeWidth="2"
          fill="none"
          className="scribble-line"
          style={{
            strokeDasharray: '4000',
            strokeDashoffset: lineDrawProgress === 1 ? '0' : `${4000 * (1 - lineDrawProgress)}`,
          }}
          onMouseEnter={() => setIsHoveringLine(true)}
          onMouseLeave={() => setIsHoveringLine(false)}
        />
        
        
        {/* Text along path - appears after line is drawn */}
        {showText && (
          <>
            <text 
              className="path-text name-text" 
              style={{ 
                fontSize: `${dynamicFontSize}px`,
                userSelect: 'none',
                pointerEvents: 'none',
                opacity: orbOpacity > 0 ? 0.05 : 1,
                transition: 'opacity 0.3s ease'
              }}
            >
              <textPath href="#textPathOffset1" startOffset="5%" dominantBaseline="auto">
                {displayedName}
              </textPath>
            </text>
            <text 
              className="path-text title-text" 
              style={{ 
                fontSize: `${dynamicFontSize * 0.7}px`,
                userSelect: 'none',
                pointerEvents: 'none',
                opacity: orbOpacity > 0 ? 0.05 : 1,
                transition: 'opacity 0.3s ease'
              }}
            >
              <textPath href="#textPathOffset2" startOffset="30%" dominantBaseline="auto">
                {displayedTitle}
              </textPath>
            </text>
          </>
        )}
        
        {/* Yellow orb that follows the path */}
        {orbPlaying && (
          <circle
            cx={orbPosition.x}
            cy={orbPosition.y}
            r={8 * orbScale}
            fill="#FFD700"
            style={{
              opacity: orbOpacity,
              filter: 'drop-shadow(0 0 8px rgba(255, 215, 0, 0.6))',
              pointerEvents: 'none',
              transition: 'r 0.15s ease-out' // Smooth scaling animation
            }}
          />
        )}
        
        {/* Control points - only visible in edit mode - rendered after text to appear on top */}
        {isEditing && controlPoints.map((point, index) => (
          <circle
            key={index}
            cx={point.x}
            cy={point.y}
            r="20"
            fill={`rgba(255, 215, 0, ${index === activeControlPoint ? 1 : getControlPointOpacity() * (orbOpacity > 0 ? 0.1 : 0.4)})`}
            stroke={`rgba(255,165,0, ${index === activeControlPoint ? 1 : getControlPointOpacity() * (orbOpacity > 0 ? 0.15 : 0.3)})`}
            strokeWidth={index === activeControlPoint ? "3" : "2"}
            className="control-point"
            style={{ 
              cursor: 'grab',
              transition: 'fill 0.2s ease, stroke 0.2s ease, stroke-width 0.2s ease',
              touchAction: 'none' // Prevent default touch behaviors
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              setIsDragging(true);
              setShowHint(false);
              const svg = svgRef.current;
              if (!svg) return;
              
              const handleMouseMove = (moveEvent: MouseEvent) => {
                // Use SVG point transformation for accurate coordinates
                try {
                  const pt = (svg as any).createSVGPoint();
                  pt.x = moveEvent.clientX;
                  pt.y = moveEvent.clientY;
                  
                  const screenCTM = svg.getScreenCTM();
                  if (screenCTM) {
                    const svgPt = pt.matrixTransform(screenCTM.inverse());
                    handlePointDrag(index, svgPt.x, svgPt.y);
                    return;
                  }
                } catch (err) {
                  // Fallback to manual calculation
                }
                
                // Fallback method
                const newRect = svg.getBoundingClientRect();
                const viewBox = svg.viewBox.baseVal;
                const scaleX = viewBox.width / newRect.width;
                const scaleY = viewBox.height / newRect.height;
                
                const x = viewBox.x + (moveEvent.clientX - newRect.left) * scaleX;
                const y = viewBox.y + (moveEvent.clientY - newRect.top) * scaleY;

                handlePointDrag(index, x, y);
              };
              
              const handleMouseUp = () => {
                setIsDragging(false);
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
                
                console.log('Mouse up (visible) - wasPlayingBeforeDrag:', wasPlayingBeforeDrag);
                // Auto-resume will handle this when orbOpacity goes to 0
              };
              
              document.addEventListener('mousemove', handleMouseMove);
              document.addEventListener('mouseup', handleMouseUp);
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(true);
              setShowHint(false);
              const svg = svgRef.current;
              if (!svg) return;
              
              const handleTouchMove = (moveEvent: TouchEvent) => {
                moveEvent.preventDefault(); // Prevent scrolling
                if (moveEvent.touches.length > 0) {
                  const touch = moveEvent.touches[0];
                  
                  // Use SVG point transformation for accurate coordinates
                  try {
                    const pt = (svg as any).createSVGPoint();
                    pt.x = touch.clientX;
                    pt.y = touch.clientY;
                    
                    const screenCTM = svg.getScreenCTM();
                    if (screenCTM) {
                      const svgPt = pt.matrixTransform(screenCTM.inverse());
                      handlePointDrag(index, svgPt.x, svgPt.y);
                      return;
                    }
                  } catch (err) {
                    // Fallback to manual calculation
                  }
                  
                  // Fallback method
                  const newRect = svg.getBoundingClientRect();
                  const viewBox = svg.viewBox.baseVal;
                  const scaleX = viewBox.width / newRect.width;
                  const scaleY = viewBox.height / newRect.height;

                  const x = viewBox.x + (touch.clientX - newRect.left) * scaleX;
                  const y = viewBox.y + (touch.clientY - newRect.top) * scaleY;
                  
                  handlePointDrag(index, x, y);
                }
              };
              
              const handleTouchEnd = (moveEvent: TouchEvent) => {
                moveEvent.preventDefault();
                setIsDragging(false);
                document.removeEventListener('touchmove', handleTouchMove);
                document.removeEventListener('touchend', handleTouchEnd);
                
                // Auto-resume will handle this when orbOpacity goes to 0
              };
              
              document.addEventListener('touchmove', handleTouchMove, { passive: false });
              document.addEventListener('touchend', handleTouchEnd, { passive: false });
            }}
          />
        ))}

        {/* Invisible larger hit areas for easier control point selection */}
        {isEditing && controlPoints.map((point, index) => (
          <circle
            key={`hit-area-${index}`}
            cx={point.x}
            cy={point.y}
            r="30"  // Double the radius of the visible control point (8 * 2)
            fill="transparent"
            stroke="none"
            className="control-point-hit-area"
            style={{ 
              cursor: 'grab',
              pointerEvents: 'all',
              touchAction: 'none'
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              setIsDragging(true);
              setShowHint(false);
              const svg = svgRef.current;
              if (!svg) return;
              
              const handleMouseMove = (moveEvent: MouseEvent) => {
                // Use SVG point transformation for accurate coordinates
                try {
                  const pt = (svg as any).createSVGPoint();
                  pt.x = moveEvent.clientX;
                  pt.y = moveEvent.clientY;
                  
                  const screenCTM = svg.getScreenCTM();
                  if (screenCTM) {
                    const svgPt = pt.matrixTransform(screenCTM.inverse());
                    handlePointDrag(index, svgPt.x, svgPt.y);
                    return;
                  }
                } catch (err) {
                  // Fallback to manual calculation
                }
                
                // Fallback method
                const newRect = svg.getBoundingClientRect();
                const viewBox = svg.viewBox.baseVal;
                const scaleX = viewBox.width / newRect.width;
                const scaleY = viewBox.height / newRect.height;
                
                const x = viewBox.x + (moveEvent.clientX - newRect.left) * scaleX;
                const y = viewBox.y + (moveEvent.clientY - newRect.top) * scaleY;
                
                handlePointDrag(index, x, y);
              };
              
              const handleMouseUp = () => {
                setIsDragging(false);
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
                
                // Auto-resume will handle this when orbOpacity goes to 0
              };
              
              document.addEventListener('mousemove', handleMouseMove);
              document.addEventListener('mouseup', handleMouseUp);
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(true);
              setShowHint(false);
              const svg = svgRef.current;
              if (!svg) return;
              
              const handleTouchMove = (moveEvent: TouchEvent) => {
                moveEvent.preventDefault(); // Prevent scrolling
                if (moveEvent.touches.length > 0) {
                  const touch = moveEvent.touches[0];
                  
                  // Use SVG point transformation for accurate coordinates
                  try {
                    const pt = (svg as any).createSVGPoint();
                    pt.x = touch.clientX;
                    pt.y = touch.clientY;
                    
                    const screenCTM = svg.getScreenCTM();
                    if (screenCTM) {
                      const svgPt = pt.matrixTransform(screenCTM.inverse());
                      handlePointDrag(index, svgPt.x, svgPt.y);
                      return;
                    }
                  } catch (err) {
                    // Fallback to manual calculation
                  }
                  
                  // Fallback method
                  const newRect = svg.getBoundingClientRect();
                  const viewBox = svg.viewBox.baseVal;
                  const scaleX = viewBox.width / newRect.width;
                  const scaleY = viewBox.height / newRect.height;
                  
                  const x = viewBox.x + (touch.clientX - newRect.left) * scaleX;
                  const y = viewBox.y + (touch.clientY - newRect.top) * scaleY;
                  
                  handlePointDrag(index, x, y);
                }
              };
              
              const handleTouchEnd = (moveEvent: TouchEvent) => {
                moveEvent.preventDefault();
                setIsDragging(false);
                document.removeEventListener('touchmove', handleTouchMove);
                document.removeEventListener('touchend', handleTouchEnd);
                
                // Auto-resume will handle this when orbOpacity goes to 0
              };
              
              document.addEventListener('touchmove', handleTouchMove, { passive: false });
              document.addEventListener('touchend', handleTouchEnd, { passive: false });
            }}
          />
        ))}

        {/* Define the path for text to follow */}
        <defs>
          <path id="textPath" d={scribblePath} />
          <path id="textPathOffset1" d={scribblePath} transform="translate(0, 0)" />
          <path id="textPathOffset2" d={scribblePath} transform="translate(0, 0)" />
        </defs>
        </svg>
        {/* Hand hint animation positioned over the SVG control point */}
        {hintPos && !isDragging && showHint && (
          <div
            className="hand-hint"
            style={{
              left: hintPos.left,
              // Position according to current hintState:
              top: hintState === 'start' ? (hintPos.top + START_OFFSET) : hintState === 'open' ? hintPos.top : hintState === 'closed' ? (hintPos.top + CLOSED_OFFSET) : hintPos.top,
              // Opacity handling:
              // - During 'start' and 'open' we use hintVisible to fade in (0 -> 1).
              // - During 'closed' we use hintFading to fade out (1 -> 0).
              opacity: hintState === 'closed' ? (hintFading ? 0 : 1) : hintState ? (hintVisible ? 1 : 0) : 0,
              transition: `top ${hintState === 'closed' ? CLOSED_DURATION : START_DURATION}ms ease, opacity ${hintState === 'closed' ? CLOSED_DURATION : START_DURATION}ms linear`
            }}
            aria-hidden
          >
            {hintState === 'start' && (
              <img className="hand-open" src="/Open.png" alt="open hand hint" />
            )}
            {hintState === 'open' && (
              <img className="hand-open" src="/Open.png" alt="open hand hint" />
            )}
            {hintState === 'closed' && (
              <img className="hand-closed" src="/Closed.png" alt="closed hand hint" />
            )}
          </div>
        )}
        
        {/* Button container - positioned below the SVG */}
        <div style={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '16px',
          zIndex: 10,
          marginTop: '10px'
        }}>
          {/* Minimal play/pause icon */}
          <button
            onClick={toggleOrbAnimation}
            style={{
              width: '32px',
              height: '32px',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease',
              fontSize: '20px',
              color: '#000000',
              fontFamily: 'monospace',
              opacity: showButtons ? 1 : 0,
              animationDelay: showButtons ? '0ms' : '0ms',
              animation: showButtons ? 'fadeInButton 0.5s ease-out forwards' : 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.2)';
              e.currentTarget.style.color = '#333333';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.color = '#000000';
            }}
          >
            {orbOpacity > 0 ? '■' : '▶'}
          </button>

          {/* Minimal speed toggle icon */}
          <button
            onClick={toggleOrbSpeed}
            style={{
              width: '32px',
              height: '32px',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease',
              fontSize: '20px',
              color: '#000000',
              fontFamily: 'monospace',
              opacity: showButtons ? 1 : 0,
              animationDelay: showButtons ? '150ms' : '0ms',
              animation: showButtons ? 'fadeInButton 0.5s ease-out forwards' : 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.2)';
              e.currentTarget.style.color = '#333333';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.color = '#000000';
            }}
            title={orbSpeed === 'fast' ? 'Switch to slow mode' : 'Switch to fast mode'}
          >
            {orbSpeed === 'fast' ? '🐰' : '🐢'}
          </button>

          {/* Minimal shuffle icon */}
          <button
            onClick={shufflePath}
            style={{
              width: '32px',
              height: '32px',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease',
              opacity: showButtons ? 1 : 0,
              animationDelay: showButtons ? '300ms' : '0ms',
              animation: showButtons ? 'fadeInButton 0.5s ease-out forwards' : 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
            title="Shuffle to next path"
          >
            <svg width="18" height="18" viewBox="0 0 998.79 715.12" style={{ fill: '#000000' }}>
              <path d="M778.41,396.48c73.26,52.03,145.21,103.12,218.44,155.13,2.59,1.84,2.59,5.69,0,7.53-72.96,51.82-144.93,102.93-218.44,155.13-3.06,2.17-7.29-.01-7.29-3.76,0-1.07,0-2.07,0-3.07,0-25.41.03-50.82,0-76.23,0-8.78-.35-9.12-8.95-9.12-47.49-.02-94.99-.08-142.48,0-54.17.1-104.62-13.34-150.25-42.63-56.98-36.58-96.26-87.17-116.78-151.94-9.02-28.48-11.3-57.82-12.83-87.54-3.15-61.18-31.93-106.93-86.99-134.99-20.35-10.37-42.36-14.74-65.25-14.7-41.7.07-83.39,0-125.09.02-17,0-32.03-5.18-44.25-17.24C-14.47,140.74-1.3,86.18,42.64,72.4c5.8-1.82,11.86-2.66,17.93-2.69,40.05-.24,80.1-.17,120.16-.15,80.87.05,149.6,29.05,205.33,87.58,32.86,34.51,54.94,75.23,66.53,121.67,6.2,24.83,7.93,50.01,9.1,75.42,2.29,49.76,24.83,88.92,64.68,118.13,27.06,19.84,57.83,29.13,91.36,29.15,48.16.04,96.33.01,144.49,0,8.72,0,8.91-.17,8.92-9.02.03-30.76.01-61.52.04-92.28,0-.02,0-.04,0-.06.01-3.7,4.23-5.82,7.24-3.68Z"/>
              <path d="M778.86.86c73.12,51.94,144.91,102.94,217.95,154.82,2.59,1.84,2.59,5.69,0,7.53-72.67,51.65-144.55,102.73-217.69,154.71-3.02,2.15-7.23.04-7.29-3.67-.35-19.75-.67-67.47-.7-92.11,0-8.77-.21-8.94-9.02-8.94-47.27,0-94.54-.02-141.82.02-51.6.04-93.75,20.01-126.36,60.01-.7.86-1.42,1.72-2.17,2.54-.29.31-.7.51-2.14,1.53-1.67-5.46-3.37-10.5-4.74-15.62-9.35-35.03-25.18-67-46.09-96.47-2.87-4.05-1.98-5.89,1.35-8.63,35.6-29.22,75.55-49.72,121.03-57.78,20.29-3.59,41.1-5.41,61.72-5.79,45.92-.84,91.86-.26,137.8-.26,10.53,0,10.5-.02,10.49-10.29-.02-20.81.16-60.22.4-77.91.05-3.72,4.26-5.84,7.29-3.69Z"/>
              <path d="M320.32,446.29c1.24,2.34,2.1,3.58,2.6,4.95,13.6,36.95,32.98,70.52,59.2,99.96,4.22,4.74,3.48,7.38-.62,11.48-37.92,38-82.72,63.95-135.24,75.19-19.89,4.26-40.5,6.48-60.85,6.99-41.66,1.05-83.37.41-125.05.27-29.7-.1-56.06-22.53-59.57-50.35-4.05-32.15,13.74-60.05,43.91-68.16,5.73-1.54,11.9-1.93,17.88-1.96,38.57-.17,77.13-.31,115.7-.05,59.53.4,105.74-23.94,138.57-73.62.83-1.25,1.79-2.42,3.49-4.7Z"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};
export default DynamicTitle;
